import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronDown, 
  Droplet, 
  Flame, 
  Filter, 
  Beaker, 
  Building, 
  Wind, 
  Battery,
  Droplets 
} from 'lucide-react';

import img from "../Images/chem.png";
import img1 from "../Images/waterside.jpg";
import img2 from "../Images/cooling.jpeg";
import img3 from "../Images/boilerfeed.jpg";
import img4 from "../Images/biocide.webp";
import img5 from "../Images/poolchemical.jpeg";
import img6 from "../Images/fire.jpeg";
import img7 from "../Images/ion.jpeg";
import img8 from "../Images/construct.jpeg";

const Chemicals = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedSubSection, setExpandedSubSection] = useState(null);

  const chemicals = {
    waterSide: {
      title: "Water Side Chemicals",
      icon: <Droplet className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Comprehensive range of water treatment chemicals for boiler systems, cooling towers, and industrial applications",
      image: img1,
      subCategories: {
        boilerFeed: {
          title: "Boiler Feed Water Chemicals",
          icon: <Droplet className="w-5 h-5 sm:w-6 sm:h-6" />,
          description: "Advanced chemical solutions for maintaining boiler efficiency, preventing scale formation, and protecting against corrosion in feed water systems.",
          image: img3,
          features: [
            "Oxygen scavengers for corrosion prevention",
            "Scale inhibitors for heat transfer efficiency",
            "pH controllers for optimal operation",
            "Sludge conditioners for better blowdown"
          ],
          specifications: [
            "pH range: 8.5-10.5",
            "Alkalinity: 300-700 ppm CaCO3",
            "Dissolved oxygen: <0.007 ppm",
            "Hardness: <0.1 ppm"
          ]
        },
        coolingWater: {
          title: "Cooling Water Chemicals",
          icon: <Wind className="w-5 h-5 sm:w-6 sm:h-6" />,
          description: "Specialized treatments for cooling water systems to prevent corrosion, scaling, and biological growth in cooling towers.",
          image: img2,
          features: [
            "Corrosion inhibitors for multiple metals",
            "Scale and deposit control agents",
            "Dispersants for suspended solids",
            "Microbiological control programs"
          ],
          specifications: [
            "pH control range: 7.0-9.0",
            "Cycles of concentration: 4-6",
            "Total dissolved solids: <2000 ppm",
            "Chloride: <500 ppm"
          ]
        },
        biocide: {
          title: "Biocide Chemicals",
          icon: <Beaker className="w-5 h-5 sm:w-6 sm:h-6" />,
          description: "Effective microbiological control solutions for water treatment systems, preventing bacterial growth and biofilm formation.",
          image: img4,
          features: [
            "Broad-spectrum antimicrobial action",
            "Non-oxidizing biocides",
            "Oxidizing biocides",
            "Biofilm removal agents"
          ],
          specifications: [
            "Active ingredient concentration: 5-50%",
            "Contact time: 2-4 hours",
            "pH stability range: 6-9",
            "Temperature stability: up to 60°C"
          ]
        },
        swimmingPool: {
          title: "Swimming Pool Chemicals",
          icon: <Droplets className="w-5 h-5 sm:w-6 sm:h-6" />,
          description: "Complete pool maintenance chemical solutions for sanitation, pH balance, and water clarity in commercial and residential pools.",
          image: img5,
          features: [
            "Chlorine-based sanitizers",
            "pH balancers and alkalinity adjusters",
            "Algaecides for prevention",
            "Water clarifiers and flocculants"
          ],
          specifications: [
            "Free chlorine: 1-3 ppm",
            "pH range: 7.2-7.6",
            "Alkalinity: 80-120 ppm",
            "Cyanuric acid: 30-80 ppm"
          ]
        }
      }
    },
    fireSide: {
      title: "Fire Side & Fuel Additive Chemicals",
      icon: <Flame className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Advanced chemical solutions for combustion efficiency, soot removal, and equipment protection in industrial boilers and furnaces.",
      image: img6,
      features: [
        "Soot removers and preventers",
        "Combustion catalysts for efficiency",
        "High-temperature corrosion inhibitors",
        "Deposit control additives"
      ],
      specifications: [
        "Treatment ratio: 1:2000-1:4000",
        "Flash point: >60°C",
        "Viscosity: 10-20 cSt at 40°C",
        "Density: 0.85-0.95 g/cm³"
      ]
    },
    ionExchange: {
      title: "Ion Exchange Resins",
      icon: <Battery className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "High-performance ion exchange resins for water softening, demineralization, and industrial water purification processes.",
      image: img7,
      features: [
        "Cation exchange resins (strong/weak acid)",
        "Anion exchange resins (strong/weak base)",
        "Mixed bed resins for ultra-pure water",
        "Specialty resins for specific applications"
      ],
      specifications: [
        "Exchange capacity: 1.8-2.0 eq/L",
        "Particle size: 0.3-1.2 mm",
        "Operating pH range: 0-14",
        "Temperature limit: 120°C"
      ]
    },
    construction: {
      title: "Construction Chemicals",
      icon: <Building className="w-6 h-6 sm:w-8 sm:h-8" />,
      description: "Innovative chemical solutions for construction, concrete applications, waterproofing, and structural protection systems.",
      image: img8,
      features: [
        "Concrete admixtures and plasticizers",
        "Waterproofing compounds and sealers",
        "Surface treatments and coatings",
        "Grouts, anchors, and repair materials"
      ],
      specifications: [
        "Compressive strength: >40 MPa",
        "Setting time: 30-60 minutes",
        "Water reduction: 15-30%",
        "Air content: 4-6%"
      ]
    }
  };

  // Structured data for chemicals page
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Industrial Chemical Solutions",
    "description": "Comprehensive range of industrial chemicals including water treatment, boiler chemicals, cooling tower treatments, and construction chemicals",
    "numberOfItems": 4,
    "itemListElement": Object.entries(chemicals).map(([key, category], index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": category.title,
        "description": category.description,
        "image": category.image
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.gorthermenergy.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Products",
        "item": "https://www.gorthermenergy.com/products"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Chemicals",
        "item": "https://www.gorthermenergy.com/products/chemicals"
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* Optimized title (under 60 characters) */}
        <title>Industrial Chemicals | Water Treatment & Boiler Solutions</title>
        
        {/* Optimized meta description (120-160 characters) */}
        <meta name="description" content="Premium industrial chemicals for water treatment, boiler systems, cooling towers & construction. Expert chemical solutions in Nepal." />
        
        {/* Keywords */}
        <meta name="keywords" content="industrial chemicals, water treatment chemicals, boiler chemicals, cooling tower chemicals, biocides, ion exchange resins, construction chemicals, Nepal" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gorthermenergy.com/products/chemicals" />
        
        {/* Language */}
        <html lang="en" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Industrial Chemical Solutions | Gortherm Energy" />
        <meta property="og:description" content="Comprehensive range of industrial chemicals for water treatment, boiler systems, and construction applications in Nepal." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/products/chemicals" />
        <meta property="og:image" content={img} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Chemical Solutions" />
        <meta name="twitter:description" content="Water treatment and boiler chemicals for industrial applications" />
        <meta name="twitter:image" content={img} />
        
        {/* Robots */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(productSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema, null, 2)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-[300px] sm:h-[400px] md:h-[500px] overflow-hidden"
          aria-label="Industrial chemical solutions hero banner"
        >
          <img
            src={img}
            alt="Industrial chemical solutions for water treatment, boiler systems, and construction applications"
            className="w-full h-full object-cover"
            loading="eager"
            width="1920"
            height="500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="max-w-2xl"
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white tracking-tight mb-4 sm:mb-6 leading-tight">
                  Industrial Chemical
                  <span className="text-blue-400"> Solutions</span>
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-gray-200 leading-relaxed">
                  Advanced chemical treatments for water treatment, boiler systems, and industrial applications across Nepal
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
          <header className="mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Chemical Solutions
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-4xl">
              We provide a comprehensive range of industrial chemical solutions designed to optimize system performance, prevent corrosion and scaling, ensure water quality, and extend equipment life across various industrial applications.
            </p>
          </header>
          
          {/* Categories Grid */}
          <div className="space-y-8 sm:space-y-12">
            {Object.entries(chemicals).map(([key, category]) => (
              <motion.article
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                {/* Category Header with Image */}
                <div className="relative h-48 sm:h-56 md:h-64">
                  <img
                    src={category.image}
                    alt={`${category.title} - ${category.description}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="1200"
                    height="400"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                    <header className="p-4 sm:p-6">
                      <div className="flex items-center space-x-2 sm:space-x-3">
                        <span className="text-white" aria-hidden="true">{category.icon}</span>
                        <h3 className="text-xl sm:text-2xl font-bold text-white">{category.title}</h3>
                      </div>
                      <p className="mt-2 text-sm sm:text-base text-gray-200">{category.description}</p>
                    </header>
                  </div>
                </div>

                {/* Category Content */}
                <div className="p-4 sm:p-6">
                  {category.subCategories ? (
                    // Water Side Chemicals with subcategories
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {Object.entries(category.subCategories).map(([subKey, subCategory]) => (
                        <motion.div
                          key={subKey}
                          className="bg-gray-50 rounded-lg overflow-hidden"
                        >
                          {/* Subcategory Image */}
                          <div className="relative h-40 sm:h-48">
                            <img
                              src={subCategory.image}
                              alt={`${subCategory.title} - ${subCategory.description}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              width="600"
                              height="300"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent">
                              <div className="absolute bottom-0 p-3 sm:p-4">
                                <div className="flex items-center space-x-2">
                                  <span className="text-white" aria-hidden="true">{subCategory.icon}</span>
                                  <h4 className="text-lg sm:text-xl font-semibold text-white">
                                    {subCategory.title}
                                  </h4>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="p-3 sm:p-4">
                            <p className="text-sm sm:text-base text-gray-600 mb-4">{subCategory.description}</p>
                            <button
                              onClick={() => setExpandedSubSection(expandedSubSection === subKey ? null : subKey)}
                              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base"
                              aria-expanded={expandedSubSection === subKey}
                            >
                              <span>View details</span>
                              <ChevronDown 
                                className={`w-4 h-4 transform transition-transform ${
                                  expandedSubSection === subKey ? 'rotate-180' : ''
                                }`}
                                aria-hidden="true"
                              />
                            </button>
                            
                            <AnimatePresence>
                              {expandedSubSection === subKey && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="mt-4 space-y-4"
                                >
                                  <div>
                                    <h5 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Features</h5>
                                    <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                                      {subCategory.features.map((feature, i) => (
                                        <li key={i}>{feature}</li>
                                      ))}
                                    </ul>
                                  </div>
                                  <div>
                                    <h5 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Technical Specifications</h5>
                                    <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                                      {subCategory.specifications.map((spec, i) => (
                                        <li key={i}>{spec}</li>
                                      ))}
                                    </ul>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    // Regular category content
                    <div>
                      <button
                        onClick={() => setExpandedSection(expandedSection === key ? null : key)}
                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base"
                        aria-expanded={expandedSection === key}
                      >
                        <span>View details</span>
                        <ChevronDown 
                          className={`w-4 h-4 transform transition-transform ${
                            expandedSection === key ? 'rotate-180' : ''
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                      
                      <AnimatePresence>
                        {expandedSection === key && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-4 space-y-4"
                          >
                            <div>
                              <h5 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Features</h5>
                              <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                                {category.features.map((feature, i) => (
                                  <li key={i}>{feature}</li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h5 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Technical Specifications</h5>
                              <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm text-gray-600">
                                {category.specifications.map((spec, i) => (
                                  <li key={i}>{spec}</li>
                                ))}
                              </ul>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </motion.article>
            ))}
          </div>

          {/* Additional SEO Content */}
          <section className="mt-12 sm:mt-16 bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Why Choose Our Chemical Solutions?
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <article>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Quality Assurance</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  All chemicals meet international quality standards with proper certifications and safety data sheets for industrial applications.
                </p>
              </article>
              
              <article>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Technical Support</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Expert guidance on chemical selection, dosing calculations, and application procedures for optimal system performance.
                </p>
              </article>
              
              <article>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Cost-Effective</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Efficient chemical formulations reduce overall treatment costs while maximizing equipment life and system efficiency.
                </p>
              </article>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Chemicals;